import type { UserFeatureFlags, TradeHubSettings } from '@sizzlenz/shared';
import * as _ from 'es-toolkit';
import { getLastSession } from '@/components/auth/AuthSessionProvider';
/**
 * Check if a user is an approved trade partner
 * (has any of: approvedBuyer, approvedDistributor, or approvedRetailer flags)
 */
export function isApprovedTradePartner(featureFlags?: UserFeatureFlags | null): boolean {
  if (!featureFlags) return false;

  return Boolean(
    featureFlags.approvedBuyer ||
    featureFlags.approvedDistributor ||
    featureFlags.approvedRetailer ||
    featureFlags.tradePartner
  );
}

/**
 * Get the specific trade partner roles a user has
 */
export function getTradePartnerRoles(featureFlags?: UserFeatureFlags | null): string[] {
  if (!featureFlags) return [];

  const roles: string[] = [];

  if (featureFlags.approvedBuyer) roles.push('Buyer');
  if (featureFlags.approvedDistributor) roles.push('Distributor');
  if (featureFlags.approvedRetailer) roles.push('Retailer');

  return roles;
}

/**
 * Check if user has a specific trade partner role
 */
export function hasTradePartnerRole(
  featureFlags: UserFeatureFlags | null | undefined,
  role: 'buyer' | 'distributor' | 'retailer'
): boolean {
  if (!featureFlags) return false;

  switch (role) {
    case 'buyer':
      return Boolean(featureFlags.approvedBuyer);
    case 'distributor':
      return Boolean(featureFlags.approvedDistributor);
    case 'retailer':
      return Boolean(featureFlags.approvedRetailer);
    default:
      return false;
  }
}

/**
 * Check if a user can access a trade hub based on visibility settings
 *
 * @param tradeHubSettings - The trade hub visibility settings
 * @param isAdmin - Whether the user is a platform admin
 * @param featureFlags - The user's feature flags
 * @param hasViewPermission - Whether the user has explicit view_trade_hub permission
 * @returns true if user can access, false if denied, null if checking/unknown
 */
export function canAccessEntityTradeHub(
  tradeHubSettings: TradeHubSettings | null | undefined,
  {
    isAdmin,
    featureFlags,
    hasViewPermission,
  }: { isAdmin?: boolean; featureFlags?: UserFeatureFlags; hasViewPermission?: boolean }
): boolean | null {
  if (!tradeHubSettings) return null;
  const sessionData = getLastSession();
  if (sessionData?.user) {
    if (_.isNil(isAdmin)) isAdmin = sessionData.user.isAdmin;
    if (_.isNil(featureFlags)) featureFlags = (sessionData.user.data as any)?.featureFlags;
  }

  // Platform admins always have access
  if (isAdmin) {
    return true;
  }

  const visibility = tradeHubSettings.visibility;
  if (hasViewPermission === true) {
    return hasViewPermission === true;
  } else if (visibility === 'public') {
    return true;
  } else if (visibility === 'user_trade_partners') {
    // Check if user has trade partner flag (any approved role)
    return featureFlags?.tradePartner === true;
  }

  return false;
}

type ContactVisibility = 'private' | 'public' | 'user_trade_partners' | 'approved_partners';

/**
 * Check if a user can access initiative contact details based on visibility settings
 *
 * @param contactVisibility - The contact visibility setting
 * @param isAdmin - Whether the user is a platform admin
 * @param featureFlags - The user's feature flags
 * @returns true if user can access, false if denied
 */
export function canAccessInitiativeContact(
  contactVisibility: ContactVisibility | undefined,
  { isAdmin, featureFlags }: { isAdmin?: boolean; featureFlags?: UserFeatureFlags } = {}
): boolean {
  // Default to public if not specified
  if (!contactVisibility) return true;

  const sessionData = getLastSession();
  if (sessionData?.user) {
    if (_.isNil(isAdmin)) isAdmin = sessionData.user.isAdmin;
    if (_.isNil(featureFlags)) featureFlags = (sessionData.user.data as any)?.featureFlags;
  }

  // Platform admins always have access
  if (isAdmin) {
    return true;
  }

  switch (contactVisibility) {
    case 'private':
      return false;
    case 'public':
      return true;
    case 'user_trade_partners':
      // Check if user has trade partner flag
      return featureFlags?.tradePartner === true;
    case 'approved_partners':
      // Check if user has any approved trade partner role
      return isApprovedTradePartner(featureFlags);
    default:
      return true;
  }
}
